'use client';

/**
 * MatrixCard — generic renderer for any BFF strategy that ships the
 * canonical `MatrixResponse` shape (`columns`, `rows`, `footer`).
 *
 * Selection-aware via `useViewportSelection`. Parameterised by:
 *   - `strategy`           — BFF strategy name to fetch (`rfq_matrix`,
 *                            future `vendor_matrix`, etc.)
 *   - `entityTypes`        — which selection types this card accepts
 *                            (others → empty state)
 *   - `paramKey`           — request param name carrying the entity id
 *                            (`rfq_sid`, `vendor_sid`, `semantic_id`, …)
 *   - `emptyStateMessage`  — copy shown when no qualifying entity is
 *                            in viewport
 *
 * Pure renderer. No sort state, no best-price logic, no formatters —
 * the server decided everything (cells carry pre-formatted `primary` /
 * `secondary` strings + `is_best` / `is_missing` flags).
 *
 * @see klyde_edge/klyde_intelligence/rfi/matrix.py — build_rfq_matrix
 * @see klyde_edge/klyde_intelligence/rfi/responses.py — MatrixResponse
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas / shared
 */

import { useEffect, useState } from 'react';
import { Loader2, LayoutGrid, Download } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';

export interface MatrixColumn {
  key: string;
  label: string;
  subtitle?: string | null;
  align?: 'left' | 'right' | 'center';
}

export interface MatrixCell {
  primary?: string | null;
  secondary?: string | null;
  is_best?: boolean;
  is_missing?: boolean;
}

export interface MatrixRow {
  row_key: string;
  label?: string | null;
  cells: Record<string, MatrixCell>;
}

export interface MatrixPayload {
  title?: string | null;
  subtitle?: string | null;
  currency?: string | null;
  columns?: MatrixColumn[];
  rows?: MatrixRow[];
  footer?: MatrixRow | null;
  /** When set, rows are clickable and fire selectNode with this type
   *  so the rail / copilot swap to the row entity's context. */
  row_entity_type?: string | null;
  empty_state?: string | null;
  /** CSV serialized server-side; the card just Blob-downloads it. */
  csv?: string | null;
  download_filename?: string | null;
}

interface MatrixCardProps {
  /** BFF strategy name. Must return a `MatrixResponse`-shaped payload. */
  strategy: string;
  /** Selection types that activate the fetch. Others show empty state. */
  entityTypes: string[];
  /** Request param key carrying the entity sid (e.g. `rfq_sid`). */
  paramKey: string;
  /** Copy shown when no qualifying entity is selected. */
  emptyStateMessage?: string;
}

export function MatrixCard({
  strategy,
  entityTypes,
  paramKey,
  emptyStateMessage = 'Select an item to see the matrix',
}: MatrixCardProps) {
  const activeTab = useTabStore((s) =>
    s.tabs.find((t) => t.id === s.activeTabId) ?? null,
  );
  const tabType = activeTab?.type ?? null;
  const tabEntityId = activeTab?.entityId ?? null;
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  // Matrix subject: re-scopes to a selected node of an accepted type (e.g.
  // an RFQ node clicked in the graph → show that RFQ's matrix), otherwise
  // the active tab. Tracked as state, NOT derived straight from
  // selectedNode, because a row-click inside the matrix flips selectedNode
  // to a sub-entity (e.g. 'product', not in entityTypes) to drive the rail —
  // that must NOT reset the subject (else the matrix would jump back to the
  // tab's RFQ). So we only re-evaluate when an accepted-type node is picked,
  // or when the selection is cleared (openTab → clearSelection → fall to tab).
  const tabSid = tabType && entityTypes.includes(tabType) ? tabEntityId : null;
  const [sid, setSid] = useState<string | null>(() =>
    selectedNode && entityTypes.includes(selectedNode.type) ? selectedNode.id : tabSid,
  );
  useEffect(() => {
    if (selectedNode && entityTypes.includes(selectedNode.type)) {
      setSid(selectedNode.id);
    } else if (!selectedNode) {
      setSid(tabSid);
    }
    // sub-entity selection (product/vendor row-click) → leave subject as-is
  }, [selectedNode, tabSid, entityTypes]);

  const { data, isLoading } = useStrategyData<MatrixPayload>(
    strategy,
    sid ? { [paramKey]: sid } : {},
    !!sid,
  );

  if (!sid) return <Shell><Empty msg={emptyStateMessage} /></Shell>;
  if (isLoading) return <Shell><Loading /></Shell>;

  // BFF returned success=false (e.g. entity not found, ACL trimmed).
  if (data && data.success === false) {
    return <Shell><Empty msg={data.error || 'Failed to load matrix'} /></Shell>;
  }

  const payload = data?.data;
  if (!payload) return <Shell><Empty msg="No data" /></Shell>;
  if (payload.empty_state) {
    return <Shell title={payload.title} subtitle={payload.subtitle}><Empty msg={payload.empty_state} /></Shell>;
  }

  const columns = payload.columns ?? [];
  const rows = payload.rows ?? [];
  const footer = payload.footer ?? null;
  const rowEntityType = payload.row_entity_type ?? null;
  const selectedRowKey =
    rowEntityType && selectedNode?.type === rowEntityType ? selectedNode.id : null;
  if (rows.length === 0) {
    return <Shell title={payload.title} subtitle={payload.subtitle}><Empty msg="No rows" /></Shell>;
  }

  // Row click → selectNode. Mirrors the legacy matrix's product-row-
  // click → copilot flow (rfq-comparison-widget.tsx handleProductClick).
  // When `row_entity_type` is null on the payload, rows are inert.
  const onRowClick = rowEntityType
    ? (row: MatrixRow) => selectNode({
        id: row.row_key,
        label: row.label ?? row.row_key,
        type: rowEntityType,
        properties: {},
      })
    : undefined;

  return (
    <Shell
      title={payload.title}
      subtitle={payload.subtitle}
      action={payload.csv ? (
        <DownloadCsvButton csv={payload.csv} filename={payload.download_filename} />
      ) : undefined}
    >
      <div className="overflow-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10 bg-muted/40 backdrop-blur-sm">
            <tr className="border-b border-border">
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-3 py-1.5 font-semibold text-muted-foreground whitespace-nowrap text-[10px] uppercase tracking-wider',
                    alignClass(col.align),
                    i > 0 && 'border-l border-border/40',
                    i === 0 ? 'min-w-[12rem] max-w-[18rem]' : 'min-w-[7rem]',
                  )}
                >
                  <div className="truncate">{col.label}</div>
                  {col.subtitle && (
                    <div className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal tabular-nums mt-0.5">
                      {col.subtitle}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const isSelected = row.row_key === selectedRowKey;
              return (
                <tr
                  key={row.row_key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-border/30 transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-accent/40',
                    !onRowClick && 'hover:bg-accent/30',
                    !isSelected && ri % 2 === 1 && 'bg-muted/10',
                    isSelected && 'bg-primary/10 ring-1 ring-inset ring-primary/30',
                  )}
                >
                  {columns.map((col, i) => (
                    <Cell
                      key={col.key}
                      cell={row.cells?.[col.key]}
                      align={col.align}
                      isFirstCol={i === 0}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
          {footer && (
            <tfoot className="sticky bottom-0 bg-card">
              <tr className="border-t-2 border-border">
                {columns.map((col, i) => (
                  <Cell
                    key={col.key}
                    cell={footer.cells?.[col.key]}
                    align={col.align}
                    isFirstCol={i === 0}
                    bold
                  />
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Shell>
  );
}

function Cell({
  cell,
  align,
  bold,
  isFirstCol,
}: {
  cell: MatrixCell | undefined;
  align?: 'left' | 'right' | 'center';
  bold?: boolean;
  isFirstCol?: boolean;
}) {
  const baseClasses = cn(
    'px-3 py-2 align-top',
    alignClass(align),
    !isFirstCol && 'border-l border-border/30',
    isFirstCol ? 'whitespace-normal max-w-[18rem]' : 'whitespace-nowrap',
  );

  if (!cell) {
    return <td className={cn(baseClasses, 'text-muted-foreground/50')}>—</td>;
  }
  return (
    <td
      className={cn(
        baseClasses,
        cell.is_missing && 'text-muted-foreground/50',
        cell.is_best && 'bg-success/10 text-success',
      )}
    >
      <div
        className={cn(
          'tabular-nums text-xs',
          bold ? 'font-semibold' : 'font-medium',
        )}
      >
        {cell.primary ?? '—'}
      </div>
      {cell.secondary && (
        <div
          className={cn(
            'text-[11px] tabular-nums mt-0.5',
            cell.is_best ? 'text-success/80' : 'text-muted-foreground',
          )}
        >
          {cell.secondary}
        </div>
      )}
    </td>
  );
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function Shell({
  title,
  subtitle,
  action,
  children,
}: {
  title?: string | null;
  subtitle?: string | null;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full w-full flex flex-col bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <LayoutGrid className="w-3.5 h-3.5 text-violet-400" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold text-foreground uppercase tracking-wider truncate">
            {title ?? 'Matrix'}
          </div>
          {subtitle && (
            <div className="text-[10px] text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>
        {action}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

// Blob-downloads the server-serialized CSV. The frontend composes
// nothing — it just hands the BFF's `csv` string to the browser.
function DownloadCsvButton({ csv, filename }: { csv: string; filename?: string | null }) {
  const onClick = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'matrix.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title="Download CSV"
      className={cn(
        'shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[10px]',
        'text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
      )}
    >
      <Download className="w-3.5 h-3.5" />
      CSV
    </button>
  );
}

function Loading() {
  return (
    <div className="h-full flex items-center justify-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4 text-muted-foreground">
      <LayoutGrid className="w-8 h-8 mb-2 opacity-40" />
      <p className="text-sm">{msg}</p>
    </div>
  );
}
